import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { quizApi } from '../services/quizApi';
import { ResultsStats, ResultsActionButtons } from '../components/results';

// Tipo per le risposte errate dal backend (fallback)
type WrongAnswersData = any[] | {
  sessionId: string;
  totalWrongAnswers: number;
  wrongAnswers: any[];
};

export const Results: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  // Dati passati dal Quiz page
  const passedData = location.state as {
    sessionData?: any;
    allAnswers?: any[];
    wrongAnswers?: any[];
    totalTimeSpent?: number;
  } | null;

  // Helper per calcolare le statistiche localmente
  const calculateStats = (session: any, wrongAnswers: any[], totalTimeSpent: number = 0) => {
    const correctAnswers = session.totalQuestions - wrongAnswers.length;
    const accuracy = session.totalQuestions > 0 ? (correctAnswers / session.totalQuestions) * 100 : 0;
    
    return {
      totalQuestions: session.totalQuestions,
      correctAnswers,
      wrongAnswers: wrongAnswers.length,
      accuracy: Math.round(accuracy * 100) / 100,
      totalTimeSpent: totalTimeSpent, // Usa il tempo totale passato dal Quiz
      averageTimePerQuestion: session.totalQuestions > 0 ? totalTimeSpent / session.totalQuestions : 0,
    };
  };

  // Usa dati locali se disponibili, altrimenti carica dal backend
  const { data: session, isLoading: sessionLoading, error: sessionError } = useQuery({
    queryKey: ['quiz-session', sessionId],
    queryFn: () => quizApi.getSession(sessionId!),
    enabled: !!sessionId && !passedData?.sessionData,
    initialData: passedData?.sessionData,
  });

  // Calcola statistiche localmente se abbiamo i dati, altrimenti carica dal backend
  const localStats = passedData ? calculateStats(
    passedData.sessionData, 
    passedData.wrongAnswers || [], 
    passedData.totalTimeSpent || 0
  ) : null;

  const { data: backendStats, isLoading: statsLoading } = useQuery({
    queryKey: ['quiz-stats', sessionId],
    queryFn: () => quizApi.getSessionStats(sessionId!),
    enabled: !!sessionId && !passedData,
  });

  // Usa statistiche locali se disponibili, altrimenti quelle del backend
  const stats = localStats || backendStats;

  // Usa risposte errate locali se disponibili, altrimenti carica dal backend
  const localWrongAnswers = passedData?.wrongAnswers || [];
  
  const { data: backendWrongAnswers, isLoading: wrongAnswersLoading } = useQuery<WrongAnswersData>({
    queryKey: ['quiz-wrong-answers', sessionId],
    queryFn: () => quizApi.getWrongAnswers(sessionId!),
    enabled: !!sessionId && !passedData,
  });

  // Helper per estrarre l'array delle risposte errate
  const getWrongAnswersArray = (data: WrongAnswersData | undefined): any[] => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return data.wrongAnswers || [];
  };

  // Usa risposte errate locali se disponibili, altrimenti quelle del backend
  const wrongAnswers = localWrongAnswers.length > 0 ? localWrongAnswers : getWrongAnswersArray(backendWrongAnswers);

  useEffect(() => {
    // Se abbiamo dati locali, non aspettiamo il backend
    if (passedData) {
      setIsLoading(false);
      return;
    }
    
    // Altrimenti aspetta che tutti i dati siano caricati dal backend
    if (sessionLoading || statsLoading || wrongAnswersLoading) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [passedData, sessionLoading, statsLoading, wrongAnswersLoading]);

  // Gestione errori
  if (sessionError) {
    return (
      <div 
        className="h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(249, 250, 251, 0.8) 0%, rgba(243, 244, 246, 0.6) 50%, rgba(249, 250, 251, 0.8) 100%)'
        }}
      >
        <div className="card text-center py-6">
          <div className="w-12 h-12 bg-danger-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-xl">❌</span>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Errore nel caricamento dei risultati
          </h2>
          <p className="text-gray-600 mb-4 text-sm">
            Non è stato possibile caricare i risultati del quiz.
          </p>
          <button
            onClick={() => navigate('/subjects')}
            className="btn-primary text-sm py-2 px-4"
          >
            Torna alle Materie
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading || !session || !stats) {
    return (
      <div 
        className="h-screen flex items-center justify-center"
        style={{
          background: 'linear-gradient(135deg, rgba(249, 250, 251, 0.8) 0%, rgba(243, 244, 246, 0.6) 50%, rgba(249, 250, 251, 0.8) 100%)'
        }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 text-sm">Caricamento risultati...</p>
        </div>
      </div>
    );
  }

  // Calcola il voto in base alla precisione
  const getGrade = (accuracy: number) => {
    if (accuracy >= 90) return { grade: 'Ottimo', color: 'text-success-600', bgColor: 'bg-success-100' };
    if (accuracy >= 80) return { grade: 'Buono', color: 'text-primary-600', bgColor: 'bg-primary-100' };
    if (accuracy >= 70) return { grade: 'Sufficiente', color: 'text-warning-600', bgColor: 'bg-warning-100' };
    return { grade: 'Da Migliorare', color: 'text-danger-600', bgColor: 'bg-danger-100' };
  };

  // Calcola il gradiente in base alla precisione
  const getGradientStyle = (accuracy: number) => {
    if (accuracy >= 60) {
      // Verde per 60-100%
      return {
        background: 'linear-gradient(135deg, rgba(240, 253, 244, 0.8) 0%, rgba(220, 252, 231, 0.6) 50%, rgba(236, 253, 245, 0.8) 100%)'
      };
    } else if (accuracy >= 50) {
      // Giallo per 50%
      return {
        background: 'linear-gradient(135deg, rgba(254, 252, 232, 0.8) 0%, rgba(254, 240, 138, 0.6) 50%, rgba(254, 249, 195, 0.8) 100%)'
      };
    } else {
      // Rosso per 0-40%
      return {
        background: 'linear-gradient(135deg, rgba(254, 242, 242, 0.8) 0%, rgba(252, 165, 165, 0.6) 50%, rgba(254, 226, 226, 0.8) 100%)'
      };
    }
  };

  const gradeInfo = getGrade(stats.accuracy);

  return (
    <div 
      className="h-full flex flex-col overflow-hidden"
      style={getGradientStyle(stats.accuracy)}
    >
      {/* Header con voto - fisso in alto */}
      <div className="text-center py-4 flex-shrink-0">
        <div className={`inline-flex items-center justify-center w-16 h-16 ${gradeInfo.bgColor} rounded-full mb-2`}>
          <span className={`text-2xl font-bold ${gradeInfo.color}`}>
            {stats.accuracy.toFixed(0)}
          </span>
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          Risultati Quiz
        </h1>
        <p className={`text-lg font-semibold ${gradeInfo.color}`}>
          {gradeInfo.grade}
        </p>
        <p className="text-gray-600 text-sm">
          {session.subjectName} • {new Date(session.updatedAt).toLocaleDateString('it-IT')}
        </p>
      </div>

      {/* Contenuto principale - scrollabile se necessario */}
      <div className="flex-1 overflow-y-auto space-y-4 px-4 pb-4">
        {/* Statistiche - Centrate */}
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            <ResultsStats stats={stats} />
          </div>
        </div>

        {/* Messaggio di congratulazioni se nessun errore - Centrato */}
        {wrongAnswers.length === 0 && (
          <div className="flex justify-center">
            <div className="card text-center py-4 w-full max-w-md">
              <div className="w-10 h-10 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-lg">🎉</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Congratulazioni!
              </h3>
              <p className="text-gray-600 text-sm">
                Hai risposto correttamente a tutte le domande.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Pulsanti di azione sopra il footer */}
      <ResultsActionButtons 
        subjectId={session.subjectId} 
        subjectName={session.subjectName}
        hasWrongAnswers={wrongAnswers.length > 0}
        questionCount={session.totalQuestions}
        wrongAnswers={wrongAnswers}
        sessionId={sessionId}
      />
    </div>
  );
};
